import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface UIState {
  sidebarOpen: boolean
  theme: 'light' | 'dark' | 'system'
  notifications: Notification[]
  loading: {
    global: boolean
    auth: boolean
    data: boolean
  }
  modals: {
    userManagement: boolean
    settings: boolean
    confirmDialog: boolean
  }
  toast: {
    show: boolean
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
    title?: string
  }
}

interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  timestamp: number
  actionUrl?: string
  actionText?: string
}

const initialState: UIState = {
  sidebarOpen: false,
  theme: 'light',
  notifications: [],
  loading: {
    global: false,
    auth: false,
    data: false
  },
  modals: {
    userManagement: false,
    settings: false,
    confirmDialog: false
  },
  toast: {
    show: false,
    type: 'info',
    message: '',
    title: ''
  }
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now()
      }
      state.notifications.unshift(notification)
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearAllNotifications: (state) => {
      state.notifications = []
    },
    setLoading: (state, action: PayloadAction<{ type: keyof UIState['loading']; value: boolean }>) => {
      state.loading[action.payload.type] = action.payload.value
    },
    setModalOpen: (state, action: PayloadAction<{ modal: keyof UIState['modals']; open: boolean }>) => {
      state.modals[action.payload.modal] = action.payload.open
    },
    showToast: (state, action: PayloadAction<{ type: 'success' | 'error' | 'warning' | 'info'; message: string; title?: string }>) => {
      state.toast = {
        show: true,
        type: action.payload.type,
        message: action.payload.message,
        title: action.payload.title
      }
    },
    hideToast: (state) => {
      state.toast.show = false
    }
  }
})

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  addNotification,
  markNotificationAsRead,
  removeNotification,
  clearAllNotifications,
  setLoading,
  setModalOpen,
  showToast,
  hideToast
} = uiSlice.actions

export default uiSlice.reducer
